import React from 'react';

import { confirmTranslationImport, previewTranslationImport, saveTranslation } from './api';

// Storefront site text as the portal's own pages write it (the Help centre,
// the Longevity Hub): one language at a time through the translation
// endpoints, English first, the way a product's page does it.
//
// A text is its English row ({ id, key, value }) and, when another language
// is being written, that language's row beside it (`translation`, or null
// where the language has no words yet and the store shows the English).

export const ENGLISH = 'en';
export const SITE_TEXT = 'storefront_site';

// The row a language writes: its own, where it has one. A text that was never
// written has a key and no row yet, in English too (its id is null).
export const rowOf = (text, language) => {
  if (!text) return null;
  if (language !== ENGLISH) return text.translation ?? null;
  return text.id === null ? null : text;
};
// The words a language has of its own: what a form starts from.
export const ownWords = (text, language) => rowOf(text, language)?.value ?? '';
// The words the store shows: the language's own, or the English where it has none.
export const wordsOf = (text, language) => (!text ? '' : ownWords(text, language) || text.value || '');
// What was typed, as it is stored: one kind of line end, no space around it.
export const unix = (value) => String(value).replace(/\r\n?/g, '\n').trim();

// The languages a page offers, English first, then by name; and the label
// each goes by in its dropdown.
export function activeLanguages(catalogue) {
  return catalogue.languages.filter((entry) => entry.active)
    .sort((left, right) => (left.code === ENGLISH ? -1 : right.code === ENGLISH ? 1 : left.name.localeCompare(right.name)));
}
export const languageLabel = (entry) => (entry.nativeName && entry.nativeName !== entry.name
  ? `${entry.name} · ${entry.nativeName}` : entry.name);

// Texts saved in one language: a text the language has a row for is changed
// in place, and one it has no row for yet is added through the import
// endpoints, which add under the English key. `changes` are [text, value];
// `source` names the page in the text history.
export async function saveTexts(changes, { language, contentTypeId, source }) {
  const added = [];
  for (const [text, value] of changes) {
    const row = rowOf(text, language);
    if (row) await saveTranslation(row.id, value);
    else if (value !== '') added.push({ translationKey: text.key, value });
  }
  if (added.length === 0) return;
  const preview = await previewTranslationImport({ contentTypeId, languageCode: language, rows: added });
  if (preview.changes.length > 0) {
    await confirmTranslationImport({
      contentTypeId, languageCode: language, changes: preview.changes, source,
    });
  }
}

// A link an article may carry: another page of the site, or a web address.
const LINK = /\[([^\]]+)\]\(((?:https?:\/\/|\/)[^)\s]*)\)/;
const INLINE = new RegExp(`${LINK.source}|\\*\\*([^*]+)\\*\\*`, 'g');

// One line's words with their marks: [words](address) a link, **words** bold.
function inline(line) {
  const out = [];
  let last = 0;
  for (const match of line.matchAll(INLINE)) {
    if (match.index > last) out.push(line.slice(last, match.index));
    out.push(match[1] !== undefined
      ? <a key={match.index} href={match[2]} target="_blank" rel="noopener noreferrer">{match[1]}</a>
      : <b key={match.index}>{match[3]}</b>);
    last = match.index + match[0].length;
  }
  if (last < line.length) out.push(line.slice(last));
  return out;
}

// A text drawn the way the store draws it: an empty line starts a paragraph,
// and a line that starts with "• " is a list item. With `marks` (a Longevity
// Hub article) a line that starts with "### " is a smaller heading, one that
// starts with "→ " is a pointer to another page, and links and bold are
// drawn; without them (a help centre answer) the words stand as they are.
export function Words({ text, marks = false, className = 'xo-words' }) {
  const blocks = [];
  const show = (line) => (marks ? inline(line) : line);
  let items = [];
  let lines = [];
  const flush = () => {
    if (lines.length > 0) {
      blocks.push(<p key={blocks.length}>{lines.map((line, at) => (
        <React.Fragment key={at}>{at > 0 && <br />}{show(line)}</React.Fragment>))}</p>);
    }
    if (items.length > 0) blocks.push(<ul key={blocks.length}>{items.map((item, at) => <li key={at}>{show(item)}</li>)}</ul>);
    lines = [];
    items = [];
  };
  for (const part of String(text).split('\n\n')) {
    for (const line of part.split('\n')) {
      // The store's article page passes over an empty line; its help page keeps it.
      if (marks && line.trim() === '') continue;
      if (marks && line.startsWith('### ')) {
        flush();
        blocks.push(<h4 key={blocks.length}>{inline(line.slice(4))}</h4>);
      } else if (line.startsWith('• ')) {
        if (lines.length > 0) flush();
        items.push(line.slice(2));
      } else {
        if (items.length > 0) flush();
        lines.push(line);
      }
    }
    flush();
  }
  return <div className={className} dir="auto">{blocks}</div>;
}
